import React, { useCallback, useEffect, useState, useReducer } from "react";
import classNames from "classnames";
import { start as startAudio, Transport } from "tone";
import { useMedia } from "react-media";
import { BrowserRouter as Router, Link, Route, Switch } from "react-router-dom";

import { TB303ControlState, TB303Waveform } from "./core";
import { appReducer, makeInitialState } from "./state";
import * as tr808 from "./audio/tr808";
import * as tb303 from "./audio/tb303";
import * as transport from "./audio/transport";
import * as magenta from "./magenta";
import { Intro } from "./Intro";
import { TR808 } from "./TR808";
import { TB303 } from "./TB303";
import { Recorder } from "./recording/Recorder";
import { RecordingTimer } from "./RecordingTimer";
import { RecordingModal } from "./recording/RecordingModal";

import "./App.scss";
import { InfoModal } from "./InfoModal";

export const App: React.FC = () => {
  let isSmallScreen = useMedia({ query: "(max-width: 768px)" });
  let isShortScreen = useMedia({ query: "(min-aspect-ratio: 10/16)" });

  let [device, setDevice] = useState<"both" | "tr808" | "tb303">(
    isSmallScreen ? "tr808" : "both"
  );
  let [inIntro, setInIntro] = useState(true);

  let [state, dispatch] = useReducer(
    appReducer,
    makeInitialState(isSmallScreen)
  );

  useEffect(() => {
    if (state.tr808.playing || state.tb303.playing) {
      let startTime =
        Transport.state === "started" ? transport.nextBeatTransportTime() : 0;
      Transport.start();
      if (state.tr808.playing) {
        let startBeat = state.tb303.playing ? transport.nextBeat() : 0;
        tr808.start(startTime, startBeat);
      }
      if (state.tb303.playing) {
        let startBeat = state.tr808.playing ? transport.nextBeat() : 0;
        tb303.start(startTime, startBeat);
      }
    }
    if (!state.tr808.playing || !state.tb303.playing) {
      if (!state.tr808.playing) {
        tr808.stop();
      }
      if (!state.tb303.playing) {
        tb303.stop();
      }
      if (!state.tr808.playing && !state.tb303.playing) {
        Transport.stop();
        Transport.seconds = 0;
      }
    }
  }, [state.tr808.playing, state.tb303.playing]);
  useEffect(() => dispatch({ type: "switchLayout", isSmallScreen }), [
    isSmallScreen,
  ]);
  useEffect(
    () =>
      tr808.applySequences(
        state.tr808.aiVariationOn
          ? [state.tr808.aiVariationSequence]
          : [state.tr808.sequence],
        state.tr808.layout.timeSteps
      ),
    [
      state.tr808.sequence,
      state.tr808.aiVariationOn,
      state.tr808.aiVariationSequence,
      state.tr808.layout.timeSteps,
    ]
  );
  useEffect(() => tr808.applyFillSequence(state.tr808.fillSequence), [
    state.tr808.fillSequence,
  ]);
  useEffect(
    () =>
      tb303.applySequence(state.tb303.sequence, state.tb303.layout.timeSteps),
    [state.tb303.sequence, state.tb303.layout.timeSteps]
  );
  useEffect(() => transport.setBpm(state.bpm), [state.bpm]);
  useEffect(() => tr808.setShuffle(state.tr808.shuffle), [state.tr808.shuffle]);
  useEffect(() => tb303.setWaveform(state.tb303.waveform), [
    state.tb303.waveform,
  ]);
  useEffect(() => tb303.setCutOffFreq(state.tb303.controls.cutOffFreq), [
    state.tb303.controls.cutOffFreq,
  ]);
  useEffect(() => tb303.setResonance(state.tb303.controls.resonance), [
    state.tb303.controls.resonance,
  ]);
  useEffect(() => tb303.setEnvMod(state.tb303.controls.envMod), [
    state.tb303.controls.envMod,
  ]);
  useEffect(() => tb303.setDecay(state.tb303.controls.decay), [
    state.tb303.controls.decay,
  ]);
  useEffect(() => tb303.setAccent(state.tb303.controls.accent), [
    state.tb303.controls.accent,
  ]);
  useEffect(() => {
    if (state.tr808.aiVariationOn) {
      magenta
        .generateVariationSequence(
          state.tr808.sequence,
          state.tr808.layout.timeSteps
        )
        .then((sequence) => dispatch({ type: "setAiVariation", sequence }));
    }
  }, [
    state.tr808.aiVariationOn,
    state.tr808.sequence,
    state.tr808.layout.timeSteps,
  ]);
  useEffect(() => {
    state.tr808.fillCount > 0 && tr808.triggerFill();
  }, [state.tr808.fillCount]);

  let startTr808 = useCallback(async () => {
    await startAudio();
    dispatch({ type: "start", machine: "tr808" });
    if (isSmallScreen) {
      dispatch({ type: "start", machine: "tb303" });
    }
  }, [isSmallScreen]);
  let stopTr808 = useCallback(() => {
    dispatch({ type: "stop", machine: "tr808" });
    if (isSmallScreen) {
      dispatch({ type: "stop", machine: "tb303" });
    }
  }, [isSmallScreen]);
  let startTb303 = useCallback(async () => {
    await startAudio();
    dispatch({ type: "start", machine: "tb303" });
  }, []);
  let stopTb303 = useCallback(() => {
    dispatch({ type: "stop", machine: "tb303" });
  }, []);
  let onToggleTr808AiVariation = useCallback((on: boolean) => {
    dispatch({ type: "toggleAiVariation", machine: "tr808", on });
  }, []);
  let toggleTr808Hit = useCallback(
    (timeStep: number, row: string, isFillHit: boolean) => {
      dispatch({
        type: "toggleHit",
        machine: "tr808",
        timeStep,
        row,
        isFillHit,
      });
    },
    []
  );
  let clearTr808Hits = useCallback(() => {
    dispatch({ type: "clearHits", machine: "tr808" });
  }, []);
  let setBpm = useCallback((bpm: number) => {
    dispatch({ type: "setBpm", bpm });
  }, []);
  let setTr808Shuffle = useCallback((shuffle: number) => {
    dispatch({ type: "setShuffle", machine: "tr808", shuffle });
  }, []);
  let triggerTr808Fill = useCallback(() => {
    dispatch({ type: "triggerFill" });
  }, []);
  let toggleTb303Hit = useCallback((timeStep: number, row: string) => {
    dispatch({ type: "toggleHit", machine: "tb303", timeStep, row });
  }, []);
  let clearTb303Hits = useCallback(() => {
    dispatch({ type: "clearHits", machine: "tb303" });
  }, []);
  let generateTb303Hits = useCallback(() => {
    dispatch({ type: "generateHits", machine: "tb303" });
  }, []);
  let adjustTb303Control = useCallback(
    (control: keyof TB303ControlState, value: number) => {
      dispatch({ type: "adjustControl", machine: "tb303", control, value });
    },
    []
  );
  let setTb303Waveform = useCallback((waveform: TB303Waveform) => {
    dispatch({ type: "setWaveform", machine: "tb303", waveform });
  }, []);

  let onOpenAbout = useCallback(() => {
    dispatch({ type: "stop", machine: "tr808" });
    dispatch({ type: "stop", machine: "tb303" });
  }, []);

  let [currentRecorder, setCurrentRecorder] = useState<Recorder>();
  let [recordingModalOpen, setRecordingModalOpen] = useState(false);
  let toggleRecording = useCallback(
    async (force = false) => {
      if (currentRecorder) {
        currentRecorder.stop();
      }
      if (!currentRecorder || force) {
        let recorder = new Recorder();
        recorder.start();
        await startAudio();
        recorder.on("stop", () => {
          dispatch({ type: "stop", machine: "tr808" });
          dispatch({ type: "stop", machine: "tb303" });
          setRecordingModalOpen(true);
        });
        setCurrentRecorder(recorder);
      }
    },
    [currentRecorder]
  );
  let onCloseRecordingModal = useCallback(
    (startNewRecording: boolean) => {
      setRecordingModalOpen(false);

      setCurrentRecorder((c) => {
        if (c) c.dispose();
        return undefined;
      });
      if (startNewRecording) {
        toggleRecording(true);
      }
    },
    [toggleRecording]
  );
  let switchDevice = useCallback(() => {
    setDevice((d) =>
      isSmallScreen ? (d === "tb303" ? "tr808" : "tb303") : "both"
    );
  }, [isSmallScreen]);

  return (
    <Router>
      <div
        className={classNames(
          "app",
          `device-${isSmallScreen ? device : "both"}`,
          { isSmallScreen, isShortScreen }
        )}
      >
        <div className="nav">
          <button
            className={classNames("button", "button--record", {
              isRecording: !!currentRecorder,
            })}
            onClick={(evt) => {
              toggleRecording(false);
              evt.currentTarget.blur();
            }}
          >
            <span className="sr-only">Record</span>
            <svg
              className="recordIcon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 41.03 41.03"
            >
              <circle className="background" cx="20.52" cy="20.52" r="16.01" />
              <circle className="outline" cx="20.52" cy="20.52" r="20.02" />
              <path
                className="text"
                d="M15.71,17.08a1.74,1.74,0,0,1,.67.5,2.34,2.34,0,0,1,.36.61,2,2,0,0,1,.14.75,2.17,2.17,0,0,1-.26,1,1.42,1.42,0,0,1-.86.7,1.39,1.39,0,0,1,.71.57,2.36,2.36,0,0,1,.21,1.13v.48a3.35,3.35,0,0,0,0,.67.63.63,0,0,0,.27.41v.18H15.33q-.06-.24-.09-.39a4.05,4.05,0,0,1-.07-.62v-.66a1.34,1.34,0,0,0-.23-.92,1.2,1.2,0,0,0-.86-.23H12.6v2.82H11.13v-7.2h3.44A3,3,0,0,1,15.71,17.08ZM12.6,18.14v1.94h1.62a1.67,1.67,0,0,0,.72-.12.81.81,0,0,0,.43-.81.91.91,0,0,0-.41-.88,1.58,1.58,0,0,0-.7-.13Z"
              />
              <path
                className="text"
                d="M23.39,18.17H19.58V19.7h3.5V21h-3.5V22.8h4v1.29H18.11v-7.2h5.28Z"
              />
              <path
                className="text"
                d="M25.18,17.64a2.94,2.94,0,0,1,2.21-.88A3,3,0,0,1,30,18a2.59,2.59,0,0,1,.49,1.34H29a1.89,1.89,0,0,0-.36-.78,1.51,1.51,0,0,0-1.2-.47,1.46,1.46,0,0,0-1.26.66,3.23,3.23,0,0,0-.47,1.87,2.89,2.89,0,0,0,.49,1.82,1.54,1.54,0,0,0,1.25.6,1.4,1.4,0,0,0,1.18-.52,2.23,2.23,0,0,0,.37-.83h1.49a3.24,3.24,0,0,1-1,1.91,2.89,2.89,0,0,1-2,.74,3.06,3.06,0,0,1-2.42-1,4,4,0,0,1-.87-2.74A4,4,0,0,1,25.18,17.64Z"
              />
            </svg>
            {currentRecorder && <RecordingTimer recorder={currentRecorder} />}
          </button>
          <Link
            to="/about"
            className="button button--about"
            onClick={onOpenAbout}
          >
            <span className="sr-only">About</span>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32.45 18.12">
              <rect width="32.45" height="1.61" />
              <rect y="16.5" width="32.45" height="1.61" />
              <rect y="8.25" width="32.45" height="1.61" />
            </svg>
          </Link>
        </div>
        <TR808
          state={state.tr808}
          bpm={state.bpm}
          isKeyboardControlsActive={!recordingModalOpen}
          onStart={startTr808}
          onStop={stopTr808}
          onSetAiVariationOn={onToggleTr808AiVariation}
          onToggleHit={toggleTr808Hit}
          onClearHits={clearTr808Hits}
          onSetBpm={setBpm}
          onSetShuffle={setTr808Shuffle}
          onTriggerFill={triggerTr808Fill}
          onSwitchDevice={switchDevice}
        />
        <TB303
          state={state.tb303}
          onStart={startTb303}
          onStop={stopTb303}
          onToggleHit={toggleTb303Hit}
          onClearHits={clearTb303Hits}
          onGenerateRandomHits={generateTb303Hits}
          onAdjustControl={adjustTb303Control}
          onSetWaveform={setTb303Waveform}
          onSwitchDevice={switchDevice}
        />
        {recordingModalOpen && currentRecorder && (
          <RecordingModal
            recorder={currentRecorder}
            onClose={onCloseRecordingModal}
          />
        )}
        <Switch>
          <Route path="/about" component={InfoModal} />
        </Switch>
        <Intro isVisible={inIntro} onDismiss={() => setInIntro(false)} />
      </div>
    </Router>
  );
};
